import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaRocket, FaSearch, FaHandshake, FaCogs } from 'react-icons/fa';

const EarlyStageTokenPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Blockchain Fund', path: '/fund/blockchain' },
    { label: 'Early Stage Token' }
  ];

  const phases = [
    {
      title: "Research & Due Diligence",
      description: "Thorough technical, market, and team analysis to identify promising early-stage token projects with significant upside potential."
    },
    {
      title: "Strategic Investment",
      description: "Participating in private sales and early funding rounds to secure favorable token acquisition terms and early adoption benefits."
    },
    {
      title: "Active Development Support",
      description: "Providing strategic guidance, industry connections, and technical insights to help projects succeed and accelerate growth."
    },
    {
      title: "Value Realization",
      description: "Strategic token position management to optimize returns as projects mature and gain market traction and liquidity."
    }
  ];

  const features = [
    {
      icon: <FaRocket className="w-8 h-8 text-secondary" />,
      title: "Growth Focus",
      description: "Targeting emerging projects with disruptive technology and exponential growth potential in the early stages."
    },
    {
      icon: <FaSearch className="w-8 h-8 text-secondary" />,
      title: "Proprietary Screening",
      description: "Advanced multi-factor screening process filters thousands of projects to identify those with highest probability of success."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-secondary" />,
      title: "Founder Access",
      description: "Direct relationships with founding teams and early access to promising projects before public sale phases."
    },
    {
      icon: <FaCogs className="w-8 h-8 text-secondary" />,
      title: "Technical Expertise",
      description: "In-house team of blockchain engineers and developers to evaluate technical merits and implementation feasibility."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Berkshire Early-Stage Token Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Capturing Early-Stage Token Value
              </h2>
              <p className="text-lg text-gray-700">
                The Berkshire Early-Stage Token Fund specializes in identifying and investing in emerging blockchain projects at their earliest stages of development, providing our investors with unique access to potentially exponential growth opportunities.
              </p>
              <p className="text-lg text-gray-700">
                Our team's deep industry connections, technical expertise, and rigorous due diligence process allow us to secure allocations in promising projects before they reach mainstream awareness, creating significant value-capture opportunities.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Learn About Investment Opportunities
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/gradient-stock-market-concept_52683-76908.jpg_m8ry2v.jpg" 
                alt="Early Stage Token Investment" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Investment Process
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We follow a structured approach to early-stage token investments, designed to maximize upside potential while carefully managing risk.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {phases.map((phase, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="inline-block bg-secondary/10 rounded-full w-10 h-10 flex items-center justify-center mb-4">
                  <span className="text-secondary font-bold text-lg">{index + 1}</span>
                </div>
                <h4 className="text-xl font-heading font-semibold mb-3">{phase.title}</h4>
                <p className="text-gray-600">{phase.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Fund Advantages
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-gray-50 dark:bg-gray-800 rounded-xl p-10 shadow-inner">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">25+</div>
                <div className="text-gray-600 dark:text-gray-300">Projects in Portfolio</div>
              </motion.div>
              
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">42%</div>
                <div className="text-gray-600 dark:text-gray-300">Average Annual ROI</div>
              </motion.div>
              
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">8</div>
                <div className="text-gray-600 dark:text-gray-300">Unicorn Projects</div>
            </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EarlyStageTokenPage;
